import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import {
  EmailBuilderAlignmentControl,
  EmailBuilderBoundedRange,
  EmailBuilderColorOverrideField,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderLineHeightControl } from "../../shell/panel/blockSettings/EmailBuilderLineHeightControl";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import { useEmailBuilderSettingsOverlayPortalId } from "../../shell/canvas/settings/emailBuilderSettingsOverlayPortal";
import { SettingSection } from "../controls/SettingSection";
import {
  baselineTextPadding,
  baselineTextTypography,
} from "../../domain/baselines";
import {
  isPaddingInactive,
} from "../settingsSectionActivation";
import {
  BLOCK_WIDTH_MIN_PX,
  blockContentMaxWidth,
  resolveBlockLayoutContext,
  resolveTextBlockWidthCommit,
} from "../../domain/block-width";
import { emailRowAreaWidth } from "../../domain/column-presets";
import {
  TEXT_PLACEHOLDERS,
  TEXT_VARIANT_OPTIONS,
  TEXT_VARIANT_STYLE,
} from "../../domain/blocks/text";
import { textHtmlHasLinks } from "../../domain/sanitize";
import {
  emailBuilderRichTextEditorHtml,
  emailBuilderRichTextStoredHtml,
} from "../../editing/richText/emailBuilderRichTextHtml";
import { selectDocument, selectEmailSettings } from "../../domain/selectors";
import { useEditorStore } from "../../domain/redux/hooks";
import { isDefaultTextHtml } from "../../domain/templates";
import {
  isLinkColorInherited,
  isTextColorInherited,
  resolveLinkColor,
  resolveTextColor,
} from "../../domain/text-theme";
import type { TextBlock, TextVariant } from "../../domain/types";
import type { BlockSettingsProps } from "./types";
import { useEmailBuilderBlockContrastAgainst } from "../useEmailBuilderBlockSurfaceColor";

const { Option } = Select;

function textVariantPatch(
  block: TextBlock,
  variant: TextVariant,
): Partial<TextBlock> {
  const style = TEXT_VARIANT_STYLE[variant];
  const next: Partial<TextBlock> = {
    variant,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    lineHeight: style.lineHeight,
  };
  if (isDefaultTextHtml(block)) {
    next.html = TEXT_PLACEHOLDERS[variant];
  } else if (variant !== "normal") {
    next.html = emailBuilderRichTextStoredHtml(
      "heading",
      emailBuilderRichTextEditorHtml({ kind: "heading", html: block.html }),
    );
  }
  return next;
}

export function TextBlockSettings({
  block,
  patch,
  textTheme,
  rowId,
  columnId,
}: BlockSettingsProps & { block: TextBlock }) {
  const { t } = useTranslation();
  const overlayPortalId = useEmailBuilderSettingsOverlayPortalId();
  const document = useEditorStore(selectDocument);
  const settings = useEditorStore(selectEmailSettings);
  const layout =
    rowId && columnId
      ? resolveBlockLayoutContext(document, rowId, columnId, block.padding)
      : null;
  const contentMaxWidth =
    layout?.contentMaxWidth ??
    blockContentMaxWidth(
      emailRowAreaWidth(settings.contentWidth, settings.pagePadding),
      block.padding,
    );
  const widthSliderMax = layout
    ? blockContentMaxWidth(layout.rowInnerWidth, block.padding)
    : contentMaxWidth;
  const typography = baselineTextTypography(block.variant);
  const inheritedTextColor =
    block.variant === "normal"
      ? textTheme.paragraphColor
      : textTheme.headingColor;
  const blockContrastAgainst = useEmailBuilderBlockContrastAgainst(rowId, columnId);

  return (
    <>
      <SettingSection sectionId="typography" title={t("Typography")}>
        <Select
          label={t("Style")}
          defaultValue={block.variant}
          onChange={(variant: TextVariant) => {
            if (variant === block.variant) {
              return;
            }
            patch(textVariantPatch(block, variant));
          }}
          usePortal
          portalId={overlayPortalId}
        >
          {TEXT_VARIANT_OPTIONS.map(option => (
            <Option key={option.value} value={option.value}>
              <span>{t(option.label)}</span>
            </Option>
          ))}
        </Select>
        <EmailBuilderBoundedRange
          label={t("Font size (px)")}
          value={block.fontSize}
          min={10}
          max={48}
          defaultValue={typography.fontSize}
          onChange={fontSize => {
            if (fontSize === undefined) {
              return;
            }
            patch({ fontSize });
          }}
        />
        <EmailBuilderLineHeightControl
          key={block.id}
          value={block.lineHeight}
          defaultValue={typography.lineHeight}
          onChange={lineHeight => {
            patch({ lineHeight });
          }}
        />
      </SettingSection>

      <SettingSection sectionId="layout" title={t("Layout")}>
        {widthSliderMax >= BLOCK_WIDTH_MIN_PX ? (
          <EmailBuilderBoundedRange
            label={t("Max width (px)")}
            value={block.widthPx ?? undefined}
            inheritValue={contentMaxWidth}
            min={BLOCK_WIDTH_MIN_PX}
            max={widthSliderMax}
            onChange={widthPx => {
              if (widthPx === undefined) {
                patch({ widthPx: null });
                return;
              }
              patch({
                widthPx: resolveTextBlockWidthCommit(widthPx, contentMaxWidth),
              });
            }}
          />
        ) : null}
        <EmailBuilderAlignmentControl
          value={block.align}
          onChange={align => patch({ align })}
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...baselineTextPadding() } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Text padding (px)")}
          value={block.padding}
          defaultValue={baselineTextPadding()}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <EmailBuilderColorOverrideField
          showFieldLabel={false}
          label={t("Text color")}
          color={resolveTextColor(block, textTheme)}
          hasOverride={!isTextColorInherited(block)}
          contrastAgainst={blockContrastAgainst}
          contrastLargeText={block.fontSize >= 18}
          onChange={color => {
            patch({
              color: color === inheritedTextColor ? null : color,
            });
          }}
          onReset={() => patch({ color: null })}
        />
        <EmailBuilderColorOverrideField
          showFieldLabel={false}
          label={t("Link color")}
          color={resolveLinkColor(block, textTheme)}
          hasOverride={!isLinkColorInherited(block)}
          contrastAgainst={
            textHtmlHasLinks(block.html) ? blockContrastAgainst : undefined
          }
          onChange={linkColor => {
            patch({
              linkColor: linkColor === textTheme.linkColor ? null : linkColor,
            });
          }}
          onReset={() => patch({ linkColor: null })}
        />
      </SettingSection>
    </>
  );
}
